import type { Variety } from "@/lib/catalog/static-varieties";
import { sameOriginPhotoUrl } from "@/lib/catalog/photo-url";

// Step 6.8: when the variety has a primary photo for the requested
// variant size, render the <img>. Otherwise fall through to the legacy
// CSS gradient + variety-name overlay. The map from variant -> photoUrls
// key is: card -> card, hero -> hero, thumbnail -> thumb.
const VARIANT_TO_PHOTO_KEY: Record<"card" | "hero" | "thumbnail", "card" | "hero" | "thumb"> = {
  card: "card",
  hero: "hero",
  thumbnail: "thumb",
};

export function GradientPlaceholder({
  variety,
  photoComingSoonLabel,
  className = "",
  variant = "card",
  imgLoading,
  fetchPriority,
}: {
  variety: Variety;
  photoComingSoonLabel: string;
  className?: string;
  // "thumbnail" omits the name text overlay (used in 28-36px row thumbs
  // where the name would just be clipped letters). "card" and "hero"
  // keep the legacy text overlay.
  variant?: "card" | "hero" | "thumbnail";
  // LCP tuning (commit 23): callers that know the image's fold position
  // can override the default eager load (e.g. catalog grid lazy-loads
  // everything past the first rows, hero gets fetchpriority=high).
  imgLoading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
}) {
  const photoKey = VARIANT_TO_PHOTO_KEY[variant];
  const photoUrl = variety.photoUrls?.primary?.[photoKey];
  const isThumb = variant === "thumbnail";

  if (photoUrl) {
    return (
      // Plain <img>: Step 6.8 photos are already sharp-resized to the
      // exact target dimensions, served as immutable WebP from
      // CloudFront. next/image's optimizer would just add a redirect
      // hop without changing pixels. eslint-disable-next-line is
      // intentional.
      // eslint-disable-next-line @next/next/no-img-element
      <img
        data-testid="variety-photo"
        data-variant={variant}
        src={sameOriginPhotoUrl(photoUrl)}
        alt={variety.name}
        loading={imgLoading ?? (isThumb ? "lazy" : "eager")}
        fetchPriority={fetchPriority}
        decoding="async"
        className={`object-cover ${className}`}
      />
    );
  }

  const useDarkText = variety.placeholderTextColor === "dark";
  const textColor = useDarkText ? "#333333" : "#FFFFFF";
  const textShadow = useDarkText
    ? "0 1px 2px rgba(255,255,255,0.6)"
    : "0 1px 2px rgba(0,0,0,0.4)";
  return (
    <div
      data-testid="gradient-placeholder"
      data-variant={variant}
      className={`relative flex items-center justify-center overflow-hidden ${className}`}
      style={{
        background: `linear-gradient(135deg, ${variety.gradientFrom} 0%, ${variety.gradientTo} 100%)`,
      }}
    >
      {!isThumb && (
        <span
          className="px-4 text-center text-xl font-semibold md:text-2xl"
          style={{ color: textColor, textShadow }}
        >
          {variety.name}
        </span>
      )}
      {!isThumb && photoComingSoonLabel && (
        <span
          className="absolute bottom-1 right-2 text-[10px] italic"
          style={{ color: textColor, opacity: 0.8 }}
        >
          {photoComingSoonLabel}
        </span>
      )}
    </div>
  );
}
